import React, { useState, useEffect } from 'react';
import '../styles/LoadedCostModal.css';

const LoadedCostModal = ({ isOpen, onClose, currentMultiplier, onSave, basePayRate = 0, employmentType = 'w2 hourly', initialFees = [{ id: 1, name: '', amount: 0 }], currency = 'USD', cadToUsdRate = 1 }) => {
  // Base percentage only applies to W2
  const BASE_W2_PERCENT = employmentType === 'w2 hourly' || employmentType === 'fulltime' ? 18 : 0;
  const AUTO_CAD_FEE_NAMES = ['Deel Fee', 'Unbillable PTO', 'Employer Costs', 'Benefit Admin Fee'];
  const W2_ONLY_CAD_FEES = ['Unbillable PTO', 'Employer Costs', 'Benefit Admin Fee'];
  const [fees, setFees] = useState(initialFees);
  
  const [workingHoursPerYear, setWorkingHoursPerYear] = useState(2080);
  const [previewPayRate, setPreviewPayRate] = useState(basePayRate || 40);
  const [useOverride, setUseOverride] = useState(false);
  const [overridePercent, setOverridePercent] = useState(parseFloat(((currentMultiplier - 1) * 100).toFixed(2)));
  const [nextFeeId, setNextFeeId] = useState(Math.max(...initialFees.map(f => f.id), 0) + 1);

  const getNextId = (feeList) => Math.max(...feeList.map(f => f.id || 0), 0) + 1;

  const computeCadAutoFees = (hoursPerYear = workingHoursPerYear, payRateValue = previewPayRate) => {
    const safeHours = hoursPerYear > 0 ? hoursPerYear : 2080;
    const hoursPerDay = safeHours / 260;
    const cadRate = Number.isFinite(cadToUsdRate) && cadToUsdRate > 0 ? cadToUsdRate : 1;
    const unbillablePtoAnnual = payRateValue > 0 ? payRateValue * hoursPerDay * 10 : 0;
    const employerCostsAnnual = 646.35 * cadRate * 5;
    const benefitAdminAnnual = 35.29 * cadRate * 5;

    return [
      { name: 'Deel Fee', amount: 599 * 12 },
      { name: 'Unbillable PTO', amount: unbillablePtoAnnual },
      { name: 'Employer Costs', amount: employerCostsAnnual },
      { name: 'Benefit Admin Fee', amount: benefitAdminAnnual }
    ];
  };

  const syncFeesForCurrency = (feeList, payRateValue = previewPayRate, hoursPerYear = workingHoursPerYear) => {
    let updatedFees = [...feeList];
    if (currency === 'CAD') {
      if (employmentType === 'c2c' || employmentType === 'c2c deel') {
        // C2C or C2C/Deel: keep only Deel Fee
        updatedFees = updatedFees.filter(fee => !AUTO_CAD_FEE_NAMES.includes(fee.name));
        let nextId = getNextId(updatedFees);
        const existingIndex = updatedFees.findIndex(fee => fee.name === 'Deel Fee');
        if (existingIndex === -1) {
          updatedFees = [{ id: nextId, name: 'Deel Fee', amount: 599 * 12 }, ...updatedFees];
        }
      } else if (employmentType === 'w2 hourly' || employmentType === 'fulltime') {
        // W2/full-time: all CAD fees
        let nextId = getNextId(updatedFees);
        computeCadAutoFees(hoursPerYear, payRateValue).forEach(({ name, amount }) => {
          const sanitizedAmount = Number.isFinite(amount) ? amount : 0;
          const existingIndex = updatedFees.findIndex(fee => fee.name === name);
          if (existingIndex !== -1) {
            updatedFees[existingIndex] = { ...updatedFees[existingIndex], amount: sanitizedAmount };
          } else {
            updatedFees = [{ id: nextId, name, amount: sanitizedAmount }, ...updatedFees];
            nextId += 1;
          }
        });
      }
    } else {
      updatedFees = updatedFees.filter(fee => !AUTO_CAD_FEE_NAMES.includes(fee.name));
    }
    return updatedFees;
  };

  useEffect(() => {
    if (isOpen) {
      const currentPercent = parseFloat(((currentMultiplier - 1) * 100).toFixed(2));
      setOverridePercent(currentPercent);
      setPreviewPayRate(basePayRate || 40);
      const syncedFees = syncFeesForCurrency(initialFees, basePayRate || 40, workingHoursPerYear);
      setFees(syncedFees);
      setNextFeeId(getNextId(syncedFees));
    }
  }, [isOpen, currentMultiplier, basePayRate, initialFees, currency, workingHoursPerYear, cadToUsdRate, employmentType]);

  useEffect(() => {
    if (!isOpen) return;
    setFees((currentFees) => {
      const updatedFees = syncFeesForCurrency(currentFees, previewPayRate, workingHoursPerYear);
      setNextFeeId(getNextId(updatedFees));
      return updatedFees;
    });
  }, [isOpen, currency, previewPayRate, workingHoursPerYear, cadToUsdRate, employmentType]);

  const handleFeeChange = (id, field, value) => {
    setFees(fees.map(fee => {
      if (fee.id === id) {
        if (field === 'amount') {
          return { ...fee, amount: parseFloat(value) || 0 };
        } else if (field === 'monthlyAmount') {
          // Convert monthly to annual (monthly * 12), rounded to 2 decimals
          const monthlyVal = parseFloat(value) || 0;
          const annualVal = Math.round(monthlyVal * 12 * 100) / 100;
          return { ...fee, amount: annualVal };
        } else {
          return { ...fee, [field]: value };
        }
      }
      return fee;
    }));
  };

  const addFee = () => {
    setFees([...fees, { id: nextFeeId, name: '', amount: 0 }]);
    setNextFeeId(nextFeeId + 1);
  };

  const removeFee = (id) => {
    setFees(fees.filter(fee => fee.id !== id));
  };

  // Calculate annual loaded costs, hourly cost, and load percentage
  const calculateLoadPercentage = () => {
    const annualLoadedCosts = fees.reduce((sum, fee) => sum + fee.amount, 0);
    const loadedCostPerHour = workingHoursPerYear > 0 ? annualLoadedCosts / workingHoursPerYear : 0;
    const additionalLoadPercent = previewPayRate > 0 ? (loadedCostPerHour / previewPayRate) * 100 : 0;
    // Add base 18% for W2 to the additional fees
    const loadPercent = BASE_W2_PERCENT + additionalLoadPercent;
    return { annualLoadedCosts, loadedCostPerHour, additionalLoadPercent, loadPercent };
  };

  const { annualLoadedCosts, loadedCostPerHour, additionalLoadPercent, loadPercent } = calculateLoadPercentage();
  const finalPercent = useOverride ? overridePercent : loadPercent;
  const finalMultiplier = 1 + finalPercent / 100;

  const handleSave = () => {
    const costsData = fees.reduce((acc, fee, idx) => {
      acc[`fee${idx + 1}`] = { name: fee.name, amount: fee.amount };
      return acc;
    }, {});
    onSave(finalMultiplier, finalPercent, costsData, fees);
    onClose();
  };

  const resetToDefault = () => {
    const resetPayRate = basePayRate || 40;
    setWorkingHoursPerYear(2080);
    setPreviewPayRate(resetPayRate);
    const resetFees = syncFeesForCurrency([{ id: 1, name: '', amount: 0 }], resetPayRate, 2080);
    setFees(resetFees);
    setNextFeeId(getNextId(resetFees));
    setUseOverride(false);
    setOverridePercent(18);
  };

  if (!isOpen) return null;

  return (
    <div className="loaded-cost-modal-overlay" onClick={onClose}>
      <div className="loaded-cost-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="loaded-cost-modal-header">
          <h2>Calculate Loaded Cost Percentage</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="tabs-container">
          <button
            className={`tab-button ${!useOverride ? 'active' : ''}`}
            onClick={() => setUseOverride(false)}
          >
            Calculated ({loadPercent.toFixed(2)}%)
          </button>
          <button
            className={`tab-button ${useOverride ? 'active' : ''}`}
            onClick={() => setUseOverride(true)}
          >
            Manual Override ({overridePercent.toFixed(2)}%)
          </button>
        </div>

        <div className="loaded-cost-modal-body">
          {!useOverride ? (
            <>
              <div className="loaded-cost-section">
                <h3>📊 Step 1: Fee Components</h3>
                <p className="section-description">
                  {employmentType === 'w2 hourly' || employmentType === 'fulltime' 
                    ? `Base W2 rate is ${BASE_W2_PERCENT}%. Add any additional fees below:` 
                    : 'Add any fees below:'}
                </p>
            
            <div className="dynamic-fees-container">
              {fees.map((fee) => {
                const monthlyAmount = fee.amount / 12;
                return (
                  <div key={fee.id} className="fee-row">
                    <div className="fee-input-group">
                      <label htmlFor={`fee-name-${fee.id}`}>Fee Name</label>
                      <input
                        id={`fee-name-${fee.id}`}
                        type="text"
                        value={fee.name}
                        onChange={(e) => handleFeeChange(fee.id, 'name', e.target.value)}
                        disabled={useOverride}
                        placeholder="e.g., Health Benefits, PTO, etc."
                      />
                    </div>
                    
                    <div className="fee-input-group">
                      <label htmlFor={`fee-monthly-${fee.id}`}>Monthly Amount</label>
                      <div className="input-with-dollar">
                        <span>$</span>
                        <input
                          id={`fee-monthly-${fee.id}`}
                          type="number"
                          min="0"
                          step="1"
                          value={Math.round(monthlyAmount)}
                          onChange={(e) => handleFeeChange(fee.id, 'monthlyAmount', e.target.value)}
                          onFocus={(e) => monthlyAmount === 0 && e.target.select()}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            handleFeeChange(fee.id, 'monthlyAmount', val.toFixed(0));
                          }}
                          disabled={useOverride}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    
                    <div className="fee-input-group">
                      <label htmlFor={`fee-amount-${fee.id}`}>Annual Amount</label>
                      <div className="input-with-dollar">
                        <span>$</span>
                        <input
                          id={`fee-amount-${fee.id}`}
                          type="number"
                          min="0"
                          step="100"
                          value={fee.amount}
                          onChange={(e) => handleFeeChange(fee.id, 'amount', e.target.value)}
                          onFocus={(e) => fee.amount === 0 && e.target.select()}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            handleFeeChange(fee.id, 'amount', val.toFixed(2));
                          }}
                          disabled={useOverride}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    
                    <button 
                      className="btn-remove-fee" 
                      onClick={() => removeFee(fee.id)}
                      disabled={useOverride}
                      title="Remove this fee"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              
              <button 
                className="btn-add-fee" 
                onClick={addFee}
                disabled={useOverride}
              >
                + Add Another Fee
              </button>
            </div>

            <div className="calculation-result">
              {BASE_W2_PERCENT > 0 && (
                <div className="calc-line">
                  <strong>Base W2 Load:</strong>
                  <span className="amount">{BASE_W2_PERCENT}%</span>
                </div>
              )}
              <div className="calc-line">
                <strong>{BASE_W2_PERCENT > 0 ? 'Additional A' : 'A'}nnual Fees:</strong>
                <span className="amount">${annualLoadedCosts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          <div className="loaded-cost-section">
            <h3>⏰ Step 2 & 3: Working Hours & Hourly Cost</h3>
            <p className="section-description">Configure your working hours per year and base pay rate:</p>
            
            <div className="hours-pay-grid">
              <div className="input-group">
                <label htmlFor="workingHours">Working Hours per Year</label>
                <div className="input-with-unit">
                  <input
                    id="workingHours"
                    type="number"
                    min="1000"
                    max="3000"
                    step="1"
                    value={workingHoursPerYear}
                    onChange={(e) => setWorkingHoursPerYear(parseFloat(e.target.value) || 2080)}
                    disabled={useOverride}
                  />
                  <span>hours</span>
                </div>
                <small>Typically 2080 (40 hrs/week × 52 weeks)</small>
              </div>

              <div className="input-group">
                <label htmlFor="basePayRate">Base Hourly Pay Rate</label>
                <div className="input-with-dollar">
                  <span>$</span>
                  <input
                    id="basePayRate"
                    type="number"
                    min="0"
                    step="0.01"
                    value={previewPayRate}
                    onChange={(e) => setPreviewPayRate(parseFloat(e.target.value) || 0)}
                    disabled={useOverride}
                    placeholder="0.00"
                  />
                </div>
                <small>Used to calculate the load percentage</small>
              </div>
            </div>

            <div className="calculation-result">
              <div className="calc-line">
                <span>Additional Cost per Hour = ${annualLoadedCosts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ÷ {workingHoursPerYear}</span>
                <strong>${loadedCostPerHour.toFixed(2)}/hr</strong>
              </div>
            </div>
          </div>

          <div className="loaded-cost-section">
            <h3>📈 Step 4: Load Percentage</h3>
            <p className="section-description">
              {BASE_W2_PERCENT > 0 
                ? `Base W2 (${BASE_W2_PERCENT}%) + Additional fees calculated from costs:` 
                : 'Total percentage calculated from fees:'}
            </p>
            
            <div className="load-result">
              {BASE_W2_PERCENT > 0 && (
                <div className="load-calc">
                  Additional Load % = (${loadedCostPerHour.toFixed(2)}/hr ÷ ${previewPayRate.toFixed(2)}) × 100 = {additionalLoadPercent.toFixed(2)}%
                </div>
              )}
              <div className="load-value">
                <span className="label">{BASE_W2_PERCENT > 0 ? 'Total' : ''} Load Percentage:</span>
                <strong>
                  {BASE_W2_PERCENT > 0 
                    ? `${BASE_W2_PERCENT}% + ${additionalLoadPercent.toFixed(2)}% = ${loadPercent.toFixed(2)}%`
                    : `${loadPercent.toFixed(2)}%`}
                </strong>
              </div>
            </div>
          </div>
            </>
          ) : (
            <>
              <div className="loaded-cost-section">
                <h3>🔧 Manual Override</h3>
                <p className="section-description">Override the calculated percentage if needed:</p>
            
            <div className="override-input-group">
              <label htmlFor="overridePercent">Load Percentage</label>
              <div className="input-with-percent">
                <input
                  id="overridePercent"
                  type="number"
                  min="0"
                  max="200"
                  step="0.1"
                  value={overridePercent}
                  onChange={(e) => setOverridePercent(parseFloat(e.target.value) || 0)}
                  disabled={!useOverride}
                />
                <span>%</span>
              </div>
            </div>
          </div>
            </>
          )}

          <div className="loaded-cost-final-result">
            <div className="final-row">
              <span>Final Load Percentage:</span>
              <strong>{finalPercent.toFixed(2)}%</strong>
            </div>
            <div className="final-row">
              <span>Multiplier for Rate Calc:</span>
              <strong>{finalMultiplier.toFixed(3)}x</strong>
            </div>
          </div>
        </div>

        <div className="loaded-cost-modal-footer">
          <button className="btn-secondary" onClick={resetToDefault}>Reset</button>
          <div className="button-group">
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
            <button className="btn-save" onClick={handleSave}>Save & Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadedCostModal;
